/**
 * PDP Mobile Click Handler v2
 *
 * Opens PhotoSwipe 5 gallery popup when a slide in .pdp-mobile-image-slider is clicked.
 * Only active on screens below 1024px.
 *
 * Key behaviours:
 * - Waits for Splide to initialise before attaching handlers
 * - Ignores drag/swipe clicks (Splide is-dragging check)
 * - Maps Splide slide index → gallery index (skips video slides)
 * - Opens PhotoSwipe at the correct image index
 * - Injects custom navigation arrows (bottom-right) matching v1 design
 * - Injects model/size info box overlay per slide via PhotoSwipe change event
 * - Injects custom close icon into PhotoSwipe toolbar
 */

document.addEventListener('DOMContentLoaded', function () {
    'use strict';

    var MAX_SCREEN_WIDTH = 1023;
    var HANDLER_ATTR = 'data-psw-v2-handler';

    /* ─── Helpers ──────────────────────────────────────────────────── */

    function isMobile() {
        return window.innerWidth <= MAX_SCREEN_WIDTH;
    }

    /**
     * Read the JSON data block rendered by pdp-gallery-popup-mobile-v2.liquid
     * Returns array of slide objects with src + dimensions + model data fields
     */
    function getGalleryData() {
        var el = document.querySelector('[id^="psw-v2-data-"]');
        if (!el) {
            console.warn('[PSW v2] Gallery data element not found');
            return [];
        }
        try {
            return JSON.parse(el.textContent);
        } catch (e) {
            console.error('[PSW v2] Failed to parse gallery data:', e);
            return [];
        }
    }

    /**
     * Get innerHTML of a <template> element by id
     */
    function getTemplate(id) {
        var tpl = document.getElementById(id);
        return tpl ? tpl.innerHTML : '';
    }

    /* ─── Index mapping ─────────────────────────────────────────────── */

    /**
     * Map a Splide slide element to its gallery (image-only) index.
     * Returns -1 if the slide contains no image (e.g. video slide).
     */
    function getGalleryIndex(clickedSlide) {
        var allSlides = document.querySelectorAll('.pdp-mobile-image-slider .splide__slide');
        var galleryIdx = 0;

        for (var i = 0; i < allSlides.length; i++) {
            var isImage = !!allSlides[i].querySelector('img');
            var isCurrent = allSlides[i] === clickedSlide;

            if (isCurrent) {
                return isImage ? galleryIdx : -1;
            }
            if (isImage) {
                galleryIdx++;
            }
        }
        return -1;
    }

    /* ─── Info box ──────────────────────────────────────────────────── */

    function buildInfoBoxHTML(item) {
        var html = '<div class="psw-v2-info-box">';

        if (item.modelName && item.modelSize) {
            html += '<div class="psw-v2-model-size">' + item.modelName + ' wears ' + item.modelSize + '</div>';
        }

        html += '<div class="psw-v2-model-details">';
        if (item.modelHeight) html += 'Height: ' + item.modelHeight + '<br>';
        if (item.modelBust)   html += 'Bust: '   + item.modelBust   + '<br>';
        if (item.modelChest)  html += 'Chest: '  + item.modelChest  + '<br>';
        if (item.modelWaist)  html += 'Waist: '  + item.modelWaist  + '<br>';
        if (item.modelHips)   html += 'Hips: '   + item.modelHips;
        html += '</div></div>';

        return html;
    }

    /* ─── PhotoSwipe open ───────────────────────────────────────────── */

    /**
     * Wait for PhotoSwipe UMD script to finish loading, then call callback.
     * Retries every 100ms up to 3 seconds.
     */
    function waitForPhotoSwipe(callback, retries) {
        retries = retries || 0;
        if (typeof PhotoSwipe !== 'undefined') {
            callback();
            return;
        }
        if (retries >= 30) {
            console.error('[PSW v2] PhotoSwipe failed to load');
            return;
        }
        setTimeout(function () {
            waitForPhotoSwipe(callback, retries + 1);
        }, 100);
    }

    function openPhotoSwipe(startIndex) {
        waitForPhotoSwipe(function () {
            _openPhotoSwipe(startIndex);
        });
    }

    function _openPhotoSwipe(startIndex) {

        var galleryData = getGalleryData();
        if (!galleryData.length) {
            console.warn('[PSW v2] No gallery data found');
            return;
        }

        var arrowIcon = getTemplate('psw-v2-icon-arrow');
        var closeIcon = getTemplate('psw-v2-icon-close');

        // Build model data map keyed by gallery index
        var modelMap = {};
        galleryData.forEach(function (item, idx) {
            if (item.hasModelData) {
                modelMap[idx] = item;
            }
        });

        // Build PhotoSwipe items — width/height required for correct zoom behaviour
        var items = galleryData.map(function (item) {
            return {
                src: item.src,
                width: item.w,
                height: item.h
            };
        });

        var pswp = new PhotoSwipe({
            dataSource: items,
            index: startIndex,
            mainClass: 'pdp-pswp',
            bgOpacity: 1,
            loop: true,
            zoom: false,       // hide zoom button — we rely on native pinch/double-tap
            close: true,
            counter: false,
            arrowPrev: false,  // hide default arrows — we inject custom bottom-right ones
            arrowNext: false,
            wheelToZoom: true,
            padding: { top: 40, bottom: 0, left: 0, right: 0 }
        });

        // ── Register custom UI elements ───────────────────────────────
        pswp.on('uiRegister', function () {

            // Custom navigation arrows (bottom-right)
            pswp.ui.registerElement({
                name: 'custom-nav',
                order: 10,
                isButton: false,
                appendTo: 'root',
                onInit: function (el) {
                    el.className = 'psw-v2-nav';
                    el.innerHTML =
                        '<button class="psw-v2-nav-prev" aria-label="Previous image">' + arrowIcon + '</button>' +
                        '<button class="psw-v2-nav-next" aria-label="Next image">'    + arrowIcon + '</button>';

                    el.querySelector('.psw-v2-nav-prev').addEventListener('click', function () {
                        pswp.prev();
                    });
                    el.querySelector('.psw-v2-nav-next').addEventListener('click', function () {
                        pswp.next();
                    });
                }
            });

            // Model/size info box (bottom-left, updates on slide change)
            pswp.ui.registerElement({
                name: 'info-box',
                order: 9,
                isButton: false,
                appendTo: 'root',
                onInit: function (el) {
                    el.className = 'psw-v2-info-box-wrap';

                    function updateInfoBox() {
                        var item = modelMap[pswp.currIndex];
                        el.innerHTML = item ? buildInfoBoxHTML(item) : '';
                    }

                    pswp.on('change', updateInfoBox);
                    updateInfoBox();
                }
            });
        });

        // ── Replace close button icon + apply drawer style ───────────
        pswp.on('afterInit', function () {
            setTimeout(function () {
                var closeBtn = pswp.element.querySelector('.pswp__button--close');
                if (closeBtn) {
                    closeBtn.classList.add('global-drawer__close');
                    if (closeIcon) {
                        closeBtn.innerHTML = closeIcon;
                    }
                }
            }, 0);
        });

        pswp.init();
    }

    /* ─── Click handler attachment ──────────────────────────────────── */

    function initClickHandlers() {
        if (!isMobile()) return;

        var slides = document.querySelectorAll('.pdp-mobile-image-slider .splide__slide');

        slides.forEach(function (slide) {
            if (slide.hasAttribute(HANDLER_ATTR)) return;
            slide.setAttribute(HANDLER_ATTR, 'true');

            slide.addEventListener('click', function () {
                // Ignore if Splide is mid-drag
                var track = slide.closest('.splide__track');
                if (track && track.classList.contains('is-dragging')) return;

                var galleryIndex = getGalleryIndex(slide);
                if (galleryIndex === -1) return; // video slide

                openPhotoSwipe(galleryIndex);
            });
        });

        console.log('[PSW v2] Click handlers attached to', slides.length, 'slides');
    }

    /* ─── Wait for Splide initialisation ───────────────────────────── */

    function waitForSplide() {
        var slider = document.querySelector('.pdp-mobile-image-slider');
        if (!slider) return;

        if (slider.classList.contains('is-initialized')) {
            initClickHandlers();
            return;
        }

        var interval = setInterval(function () {
            if (slider.classList.contains('is-initialized')) {
                clearInterval(interval);
                setTimeout(initClickHandlers, 100);
            }
        }, 100);

        // Fallback after 5 seconds
        setTimeout(function () {
            clearInterval(interval);
            initClickHandlers();
        }, 5000);
    }

    /* ─── Init ──────────────────────────────────────────────────────── */

    waitForSplide();

    window.addEventListener('load', function () {
        setTimeout(waitForSplide, 100);
    });

    // Re-init when viewport resizes into mobile range (DevTools testing support)
    var wasDesktop = window.innerWidth > MAX_SCREEN_WIDTH;
    var resizeTimer;
    window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
            var isDesktopNow = window.innerWidth > MAX_SCREEN_WIDTH;
            if (wasDesktop && !isDesktopNow) {
                waitForSplide();
            }
            wasDesktop = isDesktopNow;
        }, 250);
    });

    // Expose for manual reinit if needed
    window.pdpMobileClickHandlerV2 = {
        reinit: initClickHandlers
    };
});
